// Constantes canônicas Quick Check NR-1 v2025-09
export const QC_CONSTANTS = {
  version: "QC-NR1 v2025-09",
  locale: "pt-BR",
  
  // Cores oficiais padronizadas
  colors: {
    vermelho: "#E53935",
    laranja: "#FB8C00", 
    amarelo: "#FDD835",
    verde: "#43A047"
  },
  
  // Classificação de maturidade por IM (0-6)
  maturityLevels: {
    classifyIM: (im) => {
      if (im <= 2.0) return "Reativo";
      if (im <= 3.0) return "Defensivo";
      if (im <= 4.4) return "Em Progresso";
      if (im <= 5.4) return "Avançado";
      return "Estratégico";
    }
  },
  
  // Classificação de exposição por proporção de positivos (Q1-Q15)
  exposureLevels: {
    classifyIE: (positivesRatio) => {
      if (positivesRatio <= 1/3) return "Vermelho";
      if (positivesRatio <= 0.5) return "Laranja";
      if (positivesRatio <= 2/3) return "Amarelo";
      return "Verde";
    }
  },
  
  // Textos canônicos de interpretação por exposição
  interpretacao_exposicao: {
    "Vermelho": {
      "label": "Vermelho (Crítico)",
      "headline": "Exposição alta a riscos psicossociais.",
      "texto": "O padrão de respostas indica baixa segurança psicológica e lacunas em justiça, carga e/ou voz. É necessário conter riscos imediatamente, formalizar proteções e estabilizar ritos básicos de liderança."
    },
    "Laranja": {
      "label": "Laranja (Alerta)", 
      "headline": "Exposição relevante que demanda intervenção.",
      "texto": "Há sinais consistentes de pressão e ambiguidade. Aja rapidamente com correções processuais e liderança próxima para evitar escalada e consolidar práticas seguras."
    },
    "Amarelo": {
      "label": "Amarelo (Atenção)",
      "headline": "Zona de atenção — monitorar e melhorar.",
      "texto": "Existem pontos sensíveis que requerem prevenção e ajustes finos. Fortaleça ritos, critérios e canais antes que se convertam em incidentes."
    },
    "Verde": {
      "label": "Verde (Adequado)",
      "headline": "Exposição baixa — práticas funcionando.",
      "texto": "Os fatores psicossociais estão sob controle. Mantenha os ritos, monitore tendências e evolua via melhoria contínua e compartilhamento de boas práticas."
    }
  },
  
  // Textos canônicos de interpretação por maturidade
  interpretacao_maturidade: {
    "Reativo": {
      "label": "Reativo",
      "texto": "Atuação esporádica e sem padrão; dependente de indivíduos; baixa previsibilidade e pouca documentação."
    },
    "Defensivo": {
      "label": "Defensivo", 
      "texto": "Práticas pontuais e políticas incipientes; requer formalização, cadência e indicadores mínimos."
    },
    "Em Progresso": {
      "label": "Em Progresso",
      "texto": "Elementos-chave estabelecidos e em ampliação; ganhos ainda irregulares entre áreas."
    },
    "Avançado": {
      "label": "Avançado",
      "texto": "Processos estáveis e medidos; integração com rotinas de gestão e boa previsibilidade."
    },
    "Estratégico": {
      "label": "Estratégico", 
      "texto": "Prática madura e integrada à estratégia; aprendizado contínuo e influência externa positiva."
    }
  },
  
  // Descrições oficiais dos eixos
  eixos_descricao: {
    "governance": {
      "rotulo": "Governança & Compliance",
      "descricao": "Políticas, papéis e patrocínio de liderança para prevenir e tratar riscos psicossociais."
    },
    "data": {
      "rotulo": "Dados & Monitoramento", 
      "descricao": "Pesquisas, indicadores e acompanhamento sistemático para orientar decisões."
    },
    "processes": {
      "rotulo": "Processos & Intervenções",
      "descricao": "Protocolos, SLAs e ações de bem-estar/encaminhamento para incidentes e prevenção."
    },
    "culture": {
      "rotulo": "Cultura & Liderança",
      "descricao": "Segurança psicológica, justiça e ritos de gestão (1:1, feedback, pactos e mediação)."
    },
    "learning": {
      "rotulo": "Aprendizado & Melhoria",
      "descricao": "PDCA, post-mortems e compartilhamento de lições aprendidas para evoluir práticas."
    }
  },
  
  // Próximos passos por quadrante (IE × IM)
  proximos_passos_quadrante: {
    "Vermelho": {
      "Reativo": {
        "titulo": "Ação urgente + Mentoria",
        "resumo": "Conter riscos imediatamente e instaurar ritos mínimos com suporte próximo à liderança.",
        "bullets": [
          "Nomear sponsor executivo e abrir war room de 30 dias para RP².",
          "Implantar protocolo antirretaliação e canais de denúncia com SLA de 72h.",
          "Recalibrar metas/carga (WIP) e publicar critérios claros de priorização.",
          "Instituir 1:1 semanais com pauta e registro mínimo por líder.",
          "Treinar líderes em segurança psicológica, mediação e feedback."
        ]
      },
      "Defensivo": {
        "titulo": "Flash Compliance + Comitê",
        "resumo": "Formalizar proteções e governança para estabilizar o ambiente rapidamente.",
        "bullets": [
          "Instalar Comitê de Riscos Psicossociais (RASCI e calendário bimestral).",
          "Aprovar política escrita de RP² e divulgar canais de denúncia.",
          "Estabelecer protocolo de resposta a incidentes com triagem e escalonamento.",
          "Implantar ritos de 1:1 quinzenais e feedback estruturado.",
          "Dashboard trimestral de indicadores e plano de 30–90 dias com responsáveis."
        ]
      },
      "Em Progresso": {
        "titulo": "Task-force + Flash Compliance",
        "resumo": "Foco cirúrgico nos hotspots com proteção institucional já formalizada.",
        "bullets": [
          "Criar task-force multidisciplinar (meta de 60 dias) com sponsor executivo.",
          "Executar Flash Compliance (antirretaliação + canais com SLA de 72h).",
          "Mapear hotspots via entrevistas rápidas e dados; priorizar Top 5 riscos.",
          "Ajustar metas/WIP e pactos de equipe; 1:1 semanais nas áreas críticas.",
          "Painel quinzenal e reporte ao comitê/sponsor com marcos 30/60/90 dias."
        ]
      },
      "Avançado": {
        "titulo": "Alertar Board",
        "resumo": "Acionamento do conselho e turnaround com governança executiva.",
        "bullets": [
          "Reportar achados ao conselho/comitê de auditoria; avaliar riscos legais e reputacionais.",
          "Nomear sponsor C-level e PMO dedicado ao turnaround de clima/segurança psicológica.",
          "Estabelecer OKRs públicos de 30/60/90 dias e critérios de sucesso.",
          "Reforçar canais de denúncia e, se necessário, investigação independente.",
          "Atualizar mapa de riscos e contingências com revisão mensal no board."
        ]
      },
      "Estratégico": {
        "titulo": "Alertar Board",
        "resumo": "Decisões estruturantes em nível estratégico para reversão rápida e sustentável.",
        "bullets": [
          "Acionar o board para ajustes em estrutura, metas e incentivos que geram risco.",
          "Manter sponsor C-level e PMO; accountability por marcos de reversão.",
          "Intervir em áreas críticas com facilitadores e apoio psicológico especializado.",
          "Comunicação executiva transparente; check-ins semanais e learning loops.",
          "Revisar incentivos/meritocracia para evitar estímulos de risco psicossocial."
        ]
      }
    },
    "Laranja": {
      "Reativo": {
        "titulo": "Flash Compliance",
        "resumo": "Implantar proteções básicas e ritos mínimos para conter a tendência de escalada.",
        "bullets": [
          "Protocolo básico de denúncia e antirretaliação; SLA de resposta em até 7 dias.",
          "Sensibilização de lideranças sobre RP² e checagem de carga de trabalho.",
          "Instituir 1:1 quinzenais com pauta e registro.",
          "Ajustes rápidos em metas e processos críticos (quick fixes).",
          "Perguntas sentinela mensais e painel simples de acompanhamento."
        ]
      },
      "Defensivo": {
        "titulo": "Assessment 360°",
        "resumo": "Ampliar diagnóstico e formalizar práticas para reduzir risco e dar previsibilidade.",
        "bullets": [
          "Diagnóstico 360° (entrevistas, dados de RH, absenteísmo, turnover).",
          "Mapa de dores e causas; priorização por impacto/viabilidade.",
          "Formalizar política de RP² e rito de feedback estruturado.",
          "Calibrar metas e critérios de justiça/reconhecimento.",
          "Plano de 90 dias com responsáveis e entregáveis."
        ]
      },
      "Em Progresso": {
        "titulo": "Assessment 360°",
        "resumo": "Aprofundar causas e padronizar práticas, escalando o que já funciona.",
        "bullets": [
          "360° focado nos clusters com maior tensão.",
          "Padronizar ritos em áreas com variação de execução.",
          "Escalar boas práticas já identificadas.",
          "Treinar líderes intermediários e multiplicadores.",
          "Definir indicadores e metas por área/squad."
        ]
      },
      "Avançado": {
        "titulo": "Intervir rápido",
        "resumo": "Sprints de contenção e comunicação clara para voltar ao baseline.",
        "bullets": [
          "Sprint de 30 dias para conter pontos quentes priorizados.",
          "Ajustar critérios de promoção/recompensa percebidos como injustos.",
          "Fortalecer canais de voz/discordância segura.",
          "Comunicar mudanças e resultados esperados.",
          "Monitoramento quinzenal até estabilização."
        ]
      },
      "Estratégico": {
        "titulo": "Intervir rápido",
        "resumo": "Correções pontuais nos incentivos e alinhamento executivo.",
        "bullets": [
          "Alinhar incentivos estratégicos que geram tensão desnecessária.",
          "Designar owners por tópico com metas claras e prazos curtos.",
          "Experimentar limites de WIP e gestão de fluxo.",
          "Compartilhar aprendizados organizacionais em canal oficial.",
          "Telemetria contínua dos indicadores sentinela."
        ]
      }
    },
    "Amarelo": {
      "Reativo": {
        "titulo": "Quick Wins",
        "resumo": "Estabelecer fundamentos de gestão e prevenção com ganhos rápidos.",
        "bullets": [
          "Implantar 1:1 quinzenais com pauta simples.",
          "Check-ins semanais de metas e priorização.",
          "Guia de feedback em 1 página e uso prático.",
          "Mural de lições aprendidas por time.",
          "Mini-pesquisas pulse mensais."
        ]
      },
      "Defensivo": {
        "titulo": "Plano de melhoria",
        "resumo": "Formalizar e dar cadência às práticas para ganho de previsibilidade.",
        "bullets": [
          "Formalizar política e fluxo de denúncias com prazos de resposta.",
          "Documentar agenda padrão de 1:1 e retro mensais.",
          "Rever critérios de promoção e publicar exemplos.",
          "Treinar liderança em segurança psicológica.",
          "Definir indicadores trimestrais por eixo."
        ]
      },
      "Em Progresso": {
        "titulo": "Plano de melhoria",
        "resumo": "Fechar lacunas do radar e escalar práticas eficazes.",
        "bullets": [
          "Priorizar e fechar lacunas com eixo < 3,1.",
          "Escalar ritos que funcionam para todas as áreas.",
          "Ajustar comunicação sobre mudanças/estabilidade.",
          "Reforçar reconhecimento e justiça processual.",
          "Estabelecer KPIs por squad/área."
        ]
      },
      "Avançado": {
        "titulo": "Consolidar",
        "resumo": "Padronizar, auditar leve e integrar com analytics.",
        "bullets": [
          "Padronizar práticas entre áreas com variações.",
          "Auditoria leve de adesão aos ritos de liderança.",
          "Integração com people analytics para alertas.",
          "Programa de reconhecimento com critérios claros.",
          "Comunidade de prática entre líderes."
        ]
      },
      "Estratégico": {
        "titulo": "Consolidar",
        "resumo": "Amarrar práticas ao plano estratégico e difundir casos.",
        "bullets": [
          "Atualizar políticas à luz dos aprendizados.",
          "Vincular metas de RP² a OKRs estratégicos.",
          "Mentoria cruzada entre áreas e sponsors.",
          "Painel executivo consolidado de indicadores.",
          "Publicar casos internos de sucesso."
        ]
      }
    },
    "Verde": {
      "Reativo": {
        "titulo": "Sensibilizar",
        "resumo": "Comunicar fundamentos e garantir ritos mínimos.",
        "bullets": [
          "Campanha interna sobre RP² e boas práticas.",
          "Talk da liderança reforçando exemplos e expectativas.",
          "Definir ritos mínimos (1:1, retrôs e pactos).",
          "Pulse trimestral para vigilância de tendências.",
          "Checklist de prevenção por time."
        ]
      },
      "Defensivo": {
        "titulo": "Roadmap curto",
        "resumo": "Formalização leve para consolidar o nível adequado.",
        "bullets": [
          "Escrever e publicar política simples de RP².",
          "Implantar 1:1 e feedback formal com cadência.",
          "Operar canais de denúncia básicos com prazos.",
          "Definir KPIs essenciais e baseline de referência.",
          "Plano de 90 dias com marcos claros."
        ]
      },
      "Em Progresso": {
        "titulo": "Compartilhar boas práticas",
        "resumo": "Escalar o que funciona e medir consistência entre áreas.",
        "bullets": [
          "Catálogo de práticas que funcionam (playbook).",
          "Brown-bags e trocas entre líderes.",
          "Replicar em áreas remanescentes com coaching.",
          "Medir e comparar resultados entre times.",
          "Reconhecer times referência."
        ]
      },
      "Avançado": {
        "titulo": "Otimizar",
        "resumo": "Aprimorar com benchmark, automação e incentivos finos.",
        "bullets": [
          "Benchmark externo e metas de evolução por eixo.",
          "Automação da coleta/telemetria de indicadores.",
          "Ajustes finos em incentivos e rituais.",
          "Experimentos controlados (A/B) de práticas de gestão.",
          "Publicar relatório interno anual de RP²."
        ]
      },
      "Estratégico": {
        "titulo": "Otimizar",
        "resumo": "Integrar definitivamente à estratégia e influenciar o ecossistema.",
        "bullets": [
          "Integrar RP² ao planejamento estratégico e remuneração variável.",
          "Programa de embaixadores e mentoria interáreas.",
          "Compartilhar case público quando aplicável.",
          "Auditoria independente periódica.",
          "Metas de maturidade por unidade de negócio."
        ]
      }
    }
  },
  
  // Métodos de cálculo determinísticos
  calculations: {
    // Calcular índices baseado nas respostas
    calculateIndices: (responses) => {
      const riskQuestions = Array.from({length: 15}, (_, i) => responses[`Q${String(i+1).padStart(2, '0')}`] || 1);
      const maturityQuestions = Array.from({length: 5}, (_, i) => responses[`Q${String(i+16).padStart(2, '0')}`] || 1);
      
      // Contar positivos (5-6)
      const countPositives = (arr) => arr.filter(v => v >= 5).length;
      
      const riskPositives = countPositives(riskQuestions);
      const maturityPositives = countPositives(maturityQuestions);
      
      // Calcular índices (0-6)
      const IE = +(((riskPositives / 15) * 6).toFixed(4));
      const IM = +(((maturityPositives / 5) * 6).toFixed(4));
      
      // IE para termômetro (0-5)
      const IE_termo = +((IE / 6 * 5).toFixed(4));
      
      // Proporção de positivos para classificação
      const positivesRatio = riskPositives / 15;
      
      return {
        IE,
        IM, 
        IE_termo,
        positivesRatio,
        riskQuestions,
        maturityQuestions
      };
    },
    
    // Calcular radar por eixo (simulado - mapeamento linear das respostas)
    calculateRadar: (maturityQuestions) => {
      return maturityQuestions.map(v => +((((v - 1) * (6/5))).toFixed(4)));
    }
  }
};